import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { hasSession } from '../lib/session'
import { useT } from '../lib/i18n'
import { Empty, Icon, Sheet, Spinner } from './ui'

interface Note {
  id: string
  title: string
  body: string | null
  created_at: string
  read_at: string | null
}

/**
 * The bell, in the app bar, on every screen.
 *
 * The owner, 2026-10-10: *"notification should be at the header always like
 * bell icon"*. Until now notifications were a ROW inside Account, two taps
 * and a scroll away, with only a dot on the Account tab to say anything had
 * happened — so the carrier whose application was just approved, or the
 * transporter who just got a request, had no reason to look.
 *
 * The mockup had this from the start: a bell in the bar with a count on it.
 *
 * It renders nothing for a guest. There are no notifications for someone
 * with no account, and the query behind it would 401.
 */
export function NotificationBell() {
  const msg = useT()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const signedIn = hasSession()

  /*
   * Shares the 'unread' key with the tab bar's dot, so one fetch feeds both
   * and marking everything read updates the two together. The list itself is
   * a separate, heavier query that only runs once the sheet is open.
   */
  const unread = useQuery<{ unread_count: number }>({
    queryKey: ['unread'],
    queryFn: () => api('/notifications?limit=1'),
    enabled: signedIn,
    refetchInterval: 60_000,
  })

  const notes = useQuery<{ items: Note[]; unread_count: number }>({
    queryKey: ['notifications'],
    queryFn: () => api('/notifications?limit=50'),
    enabled: signedIn && open,
  })

  const markAll = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST', body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
      void qc.invalidateQueries({ queryKey: ['unread'] })
    },
  })

  if (!signedIn) return null

  const count = unread.data?.unread_count ?? 0
  const items = notes.data?.items ?? []

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={msg('nav.notifications')}
        className="relative grid h-8 w-8 shrink-0 place-items-center rounded-full
                   border border-line bg-board text-ink-2 transition-colors
                   hover:border-line-2 hover:text-ink"
      >
        <Icon name="bell" className="h-[15px] w-[15px]" />
        {count > 0 && (
          /* The count, not a dot: "3 things happened" is a reason to tap and
             "something happened" is not. Past nine it stops counting, because
             the exact number stops mattering and the badge stops fitting. */
          <span className="absolute -end-1 -top-1 grid h-[17px] min-w-[17px] place-items-center
                           rounded-full bg-band-red px-1 font-mono text-[9px]
                           font-bold leading-none text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title={msg('nav.notifications')} full>
        {notes.isLoading && <Spinner />}

        {!notes.isLoading && items.length === 0 && (
          <Empty title={msg('notifications.nothingYet')}
                 hint={msg('notifications.bellHint')} />
        )}

        {items.length > 0 && (
          <>
            {count > 0 && (
              <button
                type="button" onClick={() => markAll.mutate()} disabled={markAll.isPending}
                className="mb-1 min-h-[38px] w-full text-[12px] font-[650] text-accent
                           disabled:opacity-40"
              >{msg('notifications.markAllRead')}</button>
            )}
            <div className="rounded-xl border border-line bg-surface px-[15px]">
              {items.map((n, i) => (
                <div key={n.id}
                     className={`grid grid-cols-[30px_minmax(0,1fr)_auto] items-start gap-[11px]
                                 py-[13px] ${i ? 'border-t border-line' : ''}`}>
                  <span className={`grid h-[30px] w-[30px] place-items-center rounded-lg ${
                    n.read_at ? 'bg-board text-ink-3' : 'bg-accent-wash text-accent'}`}>
                    <Icon name="check" className="h-[15px] w-[15px]" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-[650]">
                      {n.title}
                      {!n.read_at && (
                        <span className="ms-1.5 inline-block h-1.5 w-1.5 rounded-full
                                         bg-accent align-middle" />
                      )}
                    </span>
                    {n.body && (
                      <span className="mt-1 block text-[11px] leading-[1.5] text-ink-3">
                        {n.body}
                      </span>
                    )}
                  </span>
                  <time className="whitespace-nowrap text-[10px] text-ink-3">
                    {ago(n.created_at, msg)}
                  </time>
                </div>
              ))}
            </div>
          </>
        )}
      </Sheet>
    </>
  )
}

/** Short relative time. The bell is a glance, not a log. */
function ago(iso: string, msg: (k: string, v?: Record<string, unknown>) => string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (mins < 1) return msg('adminq.justNow')
  if (mins < 60) return msg('time.minShort', { count: mins })
  const hours = Math.round(mins / 60)
  if (hours < 24) return msg('time.hourShort', { count: hours })
  return msg('time.dayShort', { count: Math.round(hours / 24) })
}
